import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import { t } from 'i18next';
import { createUseStyles } from 'react-jss';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaStoreRegularSvgIcon,
  FaStoreSolidSvgIcon,
  FaUserLightSvgIcon,
  FaUsersLightSvgIcon,
  FaUsersSolidSvgIcon,
} from '../../assets/icons/svgs';
import useIsLoggedIn from '../../hooks/useIsLoggedIn';

import {
  MESSAGES_INDICATOR_STATS_INITIAL_STATE,
  updateUserIndicatorStats,
} from '../../redux/user/userSlice';
import { colors, hexToRgbA } from '../../theme/colors';
import { isMobileScreen } from '../../theme/spacing';
import { fontSize } from '../../theme/typography';
import MessagesNavigateButton from '../router/Buttons/MessagesNavigateButton';
import {
  isLocationPathMatchingRoute,
  ROUTE_PATH_NAMES,
} from '../router/routePathNames';
import { dispatchClearSearchFiltersEvent } from '../SearchAssetsPage/events';
import { BOTTOM_MENU_HEIGHT } from './constants';

export const BOTTOM_MENU_NAVIGATION_HEIGHT = BOTTOM_MENU_HEIGHT;

export const BOTTOM_MENU_ITEM_ICON_CONTAINER_HEIGHT = 28;

export const BOTTOM_MENU_SVG_ITEM_ICON_SIZE = 22;
const labelFontSize = isMobileScreen ? fontSize.xxs : fontSize.xs + 1;
const useStyles = createUseStyles({
  bottomNavigation: {
    borderTop: `1px solid ${hexToRgbA(colors.backgroundLight, 0.3)}`,
    minHeight: BOTTOM_MENU_NAVIGATION_HEIGHT,
    height: BOTTOM_MENU_NAVIGATION_HEIGHT,
    maxHeight: BOTTOM_MENU_NAVIGATION_HEIGHT,
    width: '100%',
    position: 'relative',
    zIndex: 1000,
  },
  action: {
    flex: 1,
    minWidth: 0,
    maxWidth: 'none',
    padding: '6px 4px',
    color: colors.textPrimary,
    '& svg': {
      color: colors.textPrimary,
    },
    '&& .MuiTouchRipple-child': {
      backgroundColor: colors.textPrimary,
    },
    transition: 'color 0.2s ease-in-out',
  },
  actionLabel: {
    color: colors.textPrimary,
    fontSize: labelFontSize,
    transition: 'color 0.2s ease-in-out',
  },
  selectedAction: {
    color: colors.textPrimary,
    '& svg': {
      color: colors.textPrimary,
      transition: 'color 0.2s ease-in-out',
    },
    '& $actionLabel': {
      fontSize: labelFontSize,
      fontWeight: 500,
    },
  },
  loginAction: {
    color: colors.textPrimary,
    '& svg, span': {
      color: colors.textPrimary,
    },
    '& span': {
      fontWeight: 'bold',
      fontSize: 14,
    },
  },
});

const BottomRouterNavigation = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isLoggedIn = useIsLoggedIn();

  const navigateTo = (pathname: string) => {
    if (pathname !== location.pathname) {
      navigate(pathname);
    }
  };

  const handleListingsClick = () => {
    const isAlreadyOnSearchPage = isLocationPathMatchingRoute({
      currentPath: location.pathname,
      routePath: ROUTE_PATH_NAMES.SEARCH,
    });

    if (isAlreadyOnSearchPage) {
      dispatchClearSearchFiltersEvent();
    } else {
      navigateTo(ROUTE_PATH_NAMES.SEARCH);
    }
  };

  const handleMyCircleClick = () => {
    navigateTo(ROUTE_PATH_NAMES.MY_CIRCLE);
  };

  const handleLoginClick = () => {
    navigateTo(ROUTE_PATH_NAMES.LOGIN);
  };

  const isExchangeAssetsPage = isLocationPathMatchingRoute({
    currentPath: location.pathname,
    routePath: ROUTE_PATH_NAMES.EXCHANGE,
  });

  const isChatPage = isLocationPathMatchingRoute({
    currentPath: location.pathname,
    routePath: ROUTE_PATH_NAMES.CHAT,
  });

  const isChatActivitiesPage = isLocationPathMatchingRoute({
    currentPath: location.pathname,
    routePath: ROUTE_PATH_NAMES.CHAT_ACTIVITIES,
  });

  const isSocialChatPage =
    isLocationPathMatchingRoute({
      currentPath: location.pathname,
      routePath: ROUTE_PATH_NAMES.SOCIAL_CHAT,
    }) ||
    isLocationPathMatchingRoute({
      currentPath: location.pathname,
      routePath: ROUTE_PATH_NAMES.SOCIAL_CHAT_PENDING,
    });

  const isSocialActivityPage = isLocationPathMatchingRoute({
    currentPath: location.pathname,
    routePath: ROUTE_PATH_NAMES.SOCIAL_ACTIVITY,
  });

  const isRootPage = isLocationPathMatchingRoute({
    currentPath: location.pathname,
    routePath: ROUTE_PATH_NAMES.ROOT,
  });
  const isMyCirclePage =
    location.pathname === ROUTE_PATH_NAMES.MY_CIRCLE ||
    location.pathname.startsWith(`${ROUTE_PATH_NAMES.MY_CIRCLE}/`);
  const isSearchPage = isLocationPathMatchingRoute({
    currentPath: location.pathname,
    routePath: ROUTE_PATH_NAMES.SEARCH,
  });
  const bottomNavigationValue = isMyCirclePage
    ? ROUTE_PATH_NAMES.MY_CIRCLE
    : isSearchPage
    ? ROUTE_PATH_NAMES.SEARCH
    : location.pathname;

  const shouldHideBottomNavigation =
    isExchangeAssetsPage ||
    isChatPage ||
    isChatActivitiesPage ||
    isSocialChatPage ||
    isSocialActivityPage ||
    isRootPage;

  return (
    <BottomNavigation
      classes={{
        root: classes.bottomNavigation,
      }}
      sx={{
        backgroundColor: colors.backgroundMediumLight,
        ...(shouldHideBottomNavigation ? { display: 'none' } : {}),
      }}
      showLabels
      value={bottomNavigationValue}
    >
      {isLoggedIn && (
        <BottomNavigationAction
          onClick={handleMyCircleClick}
          value={ROUTE_PATH_NAMES.MY_CIRCLE}
          classes={{
            selected: classes.selectedAction,
            root: classes.action,
            label: classes.actionLabel,
          }}
          label={t('bottomMenu.myCircle')}
          icon={
            <div style={{ height: BOTTOM_MENU_ITEM_ICON_CONTAINER_HEIGHT }}>
              {isMyCirclePage ? (
                <FaUsersSolidSvgIcon size={BOTTOM_MENU_SVG_ITEM_ICON_SIZE} />
              ) : (
                <FaUsersLightSvgIcon size={BOTTOM_MENU_SVG_ITEM_ICON_SIZE} />
              )}
            </div>
          }
        />
      )}

      <BottomNavigationAction
        onClick={handleListingsClick}
        value={ROUTE_PATH_NAMES.SEARCH}
        classes={{
          selected: classes.selectedAction,
          root: classes.action,
          label: classes.actionLabel,
        }}
        label={t('bottomMenu.listings')}
        icon={
          <div style={{ height: BOTTOM_MENU_ITEM_ICON_CONTAINER_HEIGHT }}>
            {isSearchPage ? (
              <FaStoreSolidSvgIcon size={BOTTOM_MENU_SVG_ITEM_ICON_SIZE} />
            ) : (
              <FaStoreRegularSvgIcon size={BOTTOM_MENU_SVG_ITEM_ICON_SIZE} />
            )}
          </div>
        }
      />

      {isLoggedIn && (
        <BottomNavigationAction
          onClick={() => {
            navigateTo(ROUTE_PATH_NAMES.CHATS);
            dispatch(
              updateUserIndicatorStats({
                ...MESSAGES_INDICATOR_STATS_INITIAL_STATE,
                hasVisitedChatsRoute: true,
              }),
            );
          }}
          value={ROUTE_PATH_NAMES.CHATS}
          classes={{
            selected: classes.selectedAction,
            root: classes.action,
            label: classes.actionLabel,
          }}
          label={t('navigation.messages')}
          icon={
            <div style={{ height: BOTTOM_MENU_ITEM_ICON_CONTAINER_HEIGHT }}>
              <MessagesNavigateButton
                size={BOTTOM_MENU_SVG_ITEM_ICON_SIZE}
                isActive={location.pathname === ROUTE_PATH_NAMES.CHATS}
              />
            </div>
          }
        />
      )}

      {!isLoggedIn && (
        <BottomNavigationAction
          onClick={handleLoginClick}
          value={ROUTE_PATH_NAMES.LOGIN}
          classes={{
            selected: classes.selectedAction,
            root: classes.action,
            label: classes.actionLabel,
          }}
          label={t('bottomMenu.signIn')}
          icon={
            <div style={{ height: BOTTOM_MENU_ITEM_ICON_CONTAINER_HEIGHT }}>
              <FaUserLightSvgIcon size={BOTTOM_MENU_SVG_ITEM_ICON_SIZE} />
            </div>
          }
        />
      )}
    </BottomNavigation>
  );
};

export default BottomRouterNavigation;
